import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Pencil, Trash2, Upload, UserPlus, Users, Building2, Mail, User as UserIcon, ChevronRight, RotateCcw, XOctagon } from 'lucide-react';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import { SearchInput } from '@/components/ui/SearchInput';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table';
import { EmptyState } from '@/components/ui/EmptyState';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Select } from '@/components/ui/Select';
import { CustomerForm } from '@/components/customers/CustomerForm';
import { CustomerImportDialog } from '@/components/customers/CustomerImportDialog';
import { CustomerExportMenu } from '@/components/customers/CustomerExportMenu';
import {
  useCustomers,
  useDeleteCustomer,
  useSetCustomerActive,
  useSetCustomersActive,
  useHardDeleteUnusedCustomers,
} from '@/hooks/useCustomers';
import { useRowSelection } from '@/hooks/useRowSelection';
import {
  BulkActionBar,
  RowCheckbox,
  SelectAllCheckbox,
  describeBulkDelete,
} from '@/components/ui/BulkSelect';
import { usePermissions } from '@/hooks/usePermissions';
import { formatNumber, formatRelative, cn } from '@/lib/format';
import type { Customer } from '@/types/domain';

type StatusFilter = 'active' | 'inactive' | 'all';

export default function Customers() {
  const { can, scopeLimited } = usePermissions();
  // Deactivate/import/create. Editing is open to everyone: without
  // customers.edit the form files an approval request instead of saving.
  const canManage = can('customers', 'edit');
  const canCreate = can('customers', 'create');
  // Permanent delete: customers.delete is what the database checks. A
  // rayon-limited role is left out, because whether a customer ever bought
  // anything is counted through the caller's RLS and their view is partial.
  const canDelete = can('customers', 'delete') && !scopeLimited;
  const canSelect = canManage || canDelete;

  const { data: customers, isLoading } = useCustomers();
  const deleteCustomer = useDeleteCustomer();
  const setActive = useSetCustomerActive();
  const setCustomersActive = useSetCustomersActive();
  const hardDeleteUnused = useHardDeleteUnusedCustomers();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');

  const [formOpen, setFormOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState<Customer | null>(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Customer | null>(null);

  const [reactivateOpen, setReactivateOpen] = useState(false);
  const [reactivateTarget, setReactivateTarget] = useState<Customer | null>(null);
  const [bulkMode, setBulkMode] = useState<'deactivate' | 'activate' | 'delete' | null>(null);

  const filtered = useMemo(() => {
    const list = customers ?? [];
    const term = search.trim().toLowerCase();
    return list
      .filter((c) => {
        if (statusFilter === 'active' && !c.isActive) return false;
        if (statusFilter === 'inactive' && c.isActive) return false;
        if (term) {
          return (
            c.name.toLowerCase().includes(term) ||
            (c.company?.toLowerCase().includes(term) ?? false) ||
            c.phone.toLowerCase().includes(term) ||
            (c.email?.toLowerCase().includes(term) ?? false)
          );
        }
        return true;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [customers, search, statusFilter]);

  // Memoised, or useRowSelection rebuilds its lookup on every render.
  const visibleIds = useMemo(() => filtered.map((c) => c.id), [filtered]);
  const selection = useRowSelection(visibleIds);

  // Summary cards are always computed over the full active set so they
  // don't flicker as the user toggles the status filter.
  const summary = useMemo(() => {
    const list = (customers ?? []).filter((c) => c.isActive);
    return {
      total: list.length,
      withCompany: list.filter((c) => !!c.company && c.company.length > 0).length,
      withEmail: list.filter((c) => !!c.email && c.email.length > 0).length,
    };
  }, [customers]);

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (c: Customer) => {
    setEditing(c);
    setFormOpen(true);
  };

  const openDelete = (c: Customer) => {
    setDeleteTarget(c);
    setDeleteOpen(true);
  };

  const openReactivate = (c: Customer) => {
    setReactivateTarget(c);
    setReactivateOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await deleteCustomer.mutateAsync(deleteTarget.id);
    setDeleteOpen(false);
    setDeleteTarget(null);
  };

  const handleReactivate = async () => {
    if (!reactivateTarget) return;
    await setActive.mutateAsync({ id: reactivateTarget.id, isActive: true });
    setReactivateOpen(false);
    setReactivateTarget(null);
  };

  // Bulk actions for the ticked rows. Activate and deactivate apply to every one
  // of them. Permanent delete removes only customers with no transactions or
  // payments -- the database would let the rest go too and orphan their
  // invoices -- and leaves those ticked, so deactivating them is one more click.
  const handleBulk = async () => {
    if (!bulkMode) return;
    try {
      if (bulkMode === 'delete') {
        const result = await hardDeleteUnused.mutateAsync(selection.selectedIds);
        selection.keepOnly(result.inUse);
        window.alert(
          describeBulkDelete(result, 'customer', 'transaksi atau pembayaran', canManage),
        );
      } else {
        await setCustomersActive.mutateAsync({
          ids: selection.selectedIds,
          isActive: bulkMode === 'activate',
        });
        selection.clear();
      }
    } catch (e) {
      // The message carries how far it got, so the ticks stay put for a retry.
      window.alert(
        e instanceof Error
          ? e.message
          : bulkMode === 'delete'
            ? 'Gagal menghapus customer'
            : 'Gagal memperbarui customer',
      );
    } finally {
      setBulkMode(null);
    }
  };

  const bulkHiddenNote =
    selection.hiddenCount > 0
      ? ` ${selection.hiddenCount} di antaranya sedang tidak terlihat karena filter.`
      : '';

  const bulkCopy = {
    activate: {
      title: 'Aktifkan customer terpilih?',
      body: `${selection.count} customer akan muncul lagi di POS picker dan pencarian.`,
      confirm: 'Aktifkan',
    },
    deactivate: {
      title: 'Nonaktifkan customer terpilih?',
      body: `${selection.count} customer akan disembunyikan dari POS picker dan pencarian. Invoice lama tetap tersimpan sebagai snapshot dan customer bisa diaktifkan lagi kapan saja.`,
      confirm: 'Nonaktifkan',
    },
    delete: {
      title: 'Hapus permanen customer terpilih?',
      body: `${selection.count} customer akan diperiksa satu per satu. Yang belum pernah bertransaksi atau membayar dihapus selamanya beserta seluruh datanya, dan tidak bisa dikembalikan. Yang sudah pernah tidak disentuh dan tetap tercentang.`,
      confirm: 'Hapus permanen',
    },
  };
  const bulkText = bulkMode ? bulkCopy[bulkMode] : null;

  return (
    <>
      <PageHeader
        overline="Phase 3"
        title="Customers"
        description="Master data customer untuk invoice & follow-up. Customer yang non-aktif disembunyikan dari POS picker."
        actions={
          <div className="flex items-center gap-2">
            <CustomerExportMenu
              customers={customers ?? []}
              filters={{ status: statusFilter, search }}
              disabled={!customers || customers.length === 0}
            />
            {canManage && (
              <Button
                variant="secondary"
                leftIcon={<Upload size={16} />}
                onClick={() => setImportOpen(true)}
              >
                Import
              </Button>
            )}
            {canCreate && (
              <Button variant="primary" leftIcon={<Plus size={16} />} onClick={openCreate}>
                New customer
              </Button>
            )}
          </div>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 mb-6">
        <Card padding="md">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
              <Users size={18} />
            </span>
            <div>
              <p className="text-overline uppercase text-ink-subtle font-medium">Total aktif</p>
              <p className="text-metric font-display font-semibold mt-1 text-ink">
                {formatNumber(summary.total)}
              </p>
            </div>
          </div>
        </Card>
        <Card padding="md">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
              <Building2 size={18} />
            </span>
            <div>
              <p className="text-overline uppercase text-ink-subtle font-medium">Dengan perusahaan</p>
              <p className="text-metric font-display font-semibold mt-1 text-ink">
                {formatNumber(summary.withCompany)}
              </p>
            </div>
          </div>
        </Card>
        <Card padding="md">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
              <Mail size={18} />
            </span>
            <div>
              <p className="text-overline uppercase text-ink-subtle font-medium">Dengan email</p>
              <p className="text-metric font-display font-semibold mt-1 text-ink">
                {formatNumber(summary.withEmail)}
              </p>
            </div>
          </div>
        </Card>
      </div>

      <Card padding="none">
        <div className="p-4 border-b border-line flex flex-col sm:flex-row gap-3">
          <div className="flex-1 min-w-0">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search by name, company, phone, or email…"
            />
          </div>
          <Select
            value={statusFilter}
            onChange={(value) => setStatusFilter(value as StatusFilter)}
            className="sm:w-40"
            aria-label="Filter by status"
          >
            <option value="active">Active only</option>
            <option value="inactive">Inactive only</option>
            <option value="all">All</option>
          </Select>
        </div>

        {canSelect && (
          <BulkActionBar
            count={selection.count}
            hiddenCount={selection.hiddenCount}
            noun="customer"
            onClear={selection.clear}
          >
            {canManage && (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<RotateCcw size={14} />}
                  onClick={() => setBulkMode('activate')}
                >
                  Aktifkan
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<Trash2 size={14} />}
                  onClick={() => setBulkMode('deactivate')}
                >
                  Nonaktifkan
                </Button>
              </>
            )}
            {/* The only red button in the bar, because it is the only one
                that cannot be undone. */}
            {canDelete && (
              <Button
                variant="danger"
                size="sm"
                leftIcon={<XOctagon size={14} />}
                onClick={() => setBulkMode('delete')}
              >
                Hapus permanen
              </Button>
            )}
          </BulkActionBar>
        )}

        {isLoading ? (
          <div className="py-16 text-center text-ink-subtle text-body">Loading customers…</div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={<UserIcon size={24} strokeWidth={1.5} />}
            title={
              search
                ? 'Tidak ada customer yang cocok'
                : statusFilter === 'inactive'
                  ? 'Tidak ada customer nonaktif'
                  : statusFilter === 'all'
                    ? 'Belum ada customer'
                    : 'Belum ada customer aktif'
            }
            description={
              search
                ? 'Coba kata kunci lain atau ubah filter.'
                : 'Tambah customer pertama kamu untuk mulai tracking invoice & follow-up.'
            }
            action={
              !search && statusFilter !== 'inactive' && canManage && (
                <Button variant="primary" leftIcon={<UserPlus size={16} />} onClick={openCreate}>
                  Add customer
                </Button>
              )
            }
          />
        ) : (
          <Table>
            <THead>
              <TR>
                {canSelect && (
                  <TH className="w-10">
                    <SelectAllCheckbox
                      checked={selection.allVisibleSelected}
                      indeterminate={selection.someVisibleSelected}
                      onChange={selection.toggleAllVisible}
                    />
                  </TH>
                )}
                <TH>Customer</TH>
                <TH>Phone</TH>
                <TH>Email</TH>
                <TH>Status</TH>
                <TH>Ditambahkan</TH>
                <TH className="text-right">Aksi</TH>
              </TR>
            </THead>
            <TBody>
              {filtered.map((c) => (
                <TR key={c.id} className={cn(selection.isSelected(c.id) && 'bg-primary/5')}>
                  {canSelect && (
                    <TD className="w-10">
                      <RowCheckbox
                        checked={selection.isSelected(c.id)}
                        onChange={() => selection.toggle(c.id)}
                        label={`Pilih ${c.name}`}
                      />
                    </TD>
                  )}
                  <TD>
                    <Link
                      to={`/customers/${c.id}`}
                      className="flex items-center gap-3 -my-3 py-3 hover:text-primary transition-colors group"
                    >
                      <span className="w-9 h-9 rounded-full bg-line/40 text-ink-subtle flex items-center justify-center flex-shrink-0">
                        <UserIcon size={15} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-ink truncate">{c.name}</p>
                        {c.company ? (
                          <p className="text-caption text-ink-subtle truncate max-w-xs flex items-center gap-1">
                            <Building2 size={11} className="flex-shrink-0" />
                            {c.company}
                          </p>
                        ) : (
                          <p className="text-caption text-ink-subtle italic">Customer perorangan</p>
                        )}
                      </div>
                      <ChevronRight
                        size={14}
                        className="text-neutral opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                      />
                    </Link>
                  </TD>
                  <TD>
                    <code className="text-caption font-mono text-ink">{c.phone}</code>
                  </TD>
                  <TD>
                    {c.email ? (
                      <span className="text-small text-ink">{c.email}</span>
                    ) : (
                      <span className="text-caption text-neutral">—</span>
                    )}
                  </TD>
                  <TD>
                    <Chip variant={c.isActive ? 'success' : 'default'}>
                      {c.isActive ? 'Active' : 'Inactive'}
                    </Chip>
                  </TD>
                  <TD>
                    <span className="text-caption text-ink-subtle">
                      {formatRelative(c.createdAt)}
                    </span>
                  </TD>
                  <TD>
                    <div className="flex justify-end gap-1">
                      {c.isActive && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEdit(c)}
                          title={canManage ? 'Edit' : 'Ajukan perubahan'}
                          aria-label="Edit"
                        >
                          <Pencil size={16} />
                        </Button>
                      )}
                      {canManage && c.isActive && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDelete(c)}
                            title="Deactivate"
                            aria-label="Deactivate"
                          >
                            <Trash2 size={16} className="text-danger" />
                          </Button>
                        </>
                      )}
                      {canManage && !c.isActive && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openReactivate(c)}
                          title="Reactivate"
                          aria-label="Reactivate"
                        >
                          <UserPlus size={16} className="text-success" />
                        </Button>
                      )}
                    </div>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </Card>

      <CustomerImportDialog open={importOpen} onClose={() => setImportOpen(false)} />

      <CustomerForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        customer={editing}
      />

      <ConfirmDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
        title="Deactivate customer?"
        description={
          deleteTarget
            ? `"${deleteTarget.name}" akan dinonaktifkan. Invoice lama tetap tersimpan (snapshot) dan customer bisa diaktifkan lagi nanti.`
            : ''
        }
        confirmText="Deactivate"
        variant="danger"
        loading={deleteCustomer.isPending}
      />

      <ConfirmDialog
        open={reactivateOpen}
        onClose={() => setReactivateOpen(false)}
        onConfirm={handleReactivate}
        title="Reactivate customer?"
        description={
          reactivateTarget
            ? `"${reactivateTarget.name}" akan muncul lagi di POS picker dan pencarian.`
            : ''
        }
        confirmText="Reactivate"
        variant="primary"
        loading={setActive.isPending}
      />

      <ConfirmDialog
        open={bulkText !== null}
        onClose={() => setBulkMode(null)}
        onConfirm={handleBulk}
        title={bulkText?.title ?? ''}
        description={bulkText ? bulkText.body + bulkHiddenNote : ''}
        confirmText={bulkText?.confirm}
        variant={bulkMode === 'activate' ? 'primary' : 'danger'}
        loading={setCustomersActive.isPending || hardDeleteUnused.isPending}
      />
    </>
  );
}
